import {
  AiCameraIcon,
  CameraIcon,
  LinkIcon,
  PollIcon,
  VideoIcon,
  QuestionIcon,
  DescriptionIcon,
  PrivatePollIcon,
} from 'components/CommonComponents/JSXIcons'
import keys from 'constants/keys'
import { Content, OpenQuestionSubtask, PollSubtask, Subtask, useFormStepContext } from '../FormStepContext'
import { useVmAiEnhance } from './useVmAiEnhance'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { FEATURE_FLAGS } from 'constants/featureFlags'
import { DEFAULT_T_RECORD_A_VIDEO, DEFAULT_T_TAKE_A_PHOTO } from '../utils'

import { AI_IMAGE_BUTTON_TYPE } from '../model/subtaskTypes'
export { AI_IMAGE_BUTTON_TYPE } from '../model/subtaskTypes'

const SUBTASK_BUTTONS = [
  {
    type: keys.SUBTASK_TYPES.DESCRIPTION,
    label: 't_desc',
    ariaLabel: 'Add description',
    Component: DescriptionIcon,
  },
  {
    type: keys.SUBTASK_TYPES.POLL_SUBTASK,
    label: 't_st_poll',
    ariaLabel: 'Add poll',
    Component: PollIcon,
  },
  {
    type: keys.SUBTASK_TYPES.PRIVATE_POLL,
    label: 't_st_quiz',
    ariaLabel: 'Add private poll',
    Component: PrivatePollIcon,
  },
  {
    type: keys.SUBTASK_TYPES.LINK_SUBTASK,
    label: 't_st_link',
    ariaLabel: 'Add link',
    Component: LinkIcon,
  },
  {
    type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
    label: 't_st_image',
    ariaLabel: 'Add image',
    Component: CameraIcon,
  },
  {
    type: AI_IMAGE_BUTTON_TYPE,
    label: 'ai_photo_subtask',
    ariaLabel: 'Add AI photo',
    Component: AiCameraIcon,
  },
  {
    type: keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
    label: 't_st_video',
    ariaLabel: 'Add video',
    Component: VideoIcon,
  },
  {
    type: keys.SUBTASK_TYPES.OPEN_QUESTION,
    label: 't_st_open_qst',
    ariaLabel: 'Add question',
    Component: QuestionIcon,
  },
]

export function useSubtaskMenu() {
  const { subtaskActions, isVmTask, isMediaUploading } = useFormStepContext()
  const { openForSubtask } = useVmAiEnhance()
  const isAiVmEnabled = useFeatureFlag(FEATURE_FLAGS.AI_VM)

  /**
   * Both dual-view hosts intercept openForSubtask through startAiSetup: they append a permanent
   * AI card and keep the workflow inside its right pane. The legacy fallback still saves the
   * whole step snapshot before navigating away and merges the generated result on return.
   */
  const handleAddAiImageSubtask = () => {
    // A snapshot taken mid-upload would capture blob placeholder URLs of the other subtasks.
    if (isMediaUploading) return

    const values = subtaskActions.snapshotSubtasks?.() ?? { sub_tasks: [] }
    // Shared startAiSetup marks this source is_ai_enhanced immediately; it cannot revert to a
    // regular photo when its local workflow closes. The legacy fallback enhances only on Done.
    const aiSubtask: Subtask = {
      title: DEFAULT_T_TAKE_A_PHOTO,
      is_required: true,
      type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
      order_index: 0,
      content: {
        text: '',
        images: [],
        videos: [],
        files: [],
      },
    }
    const sub_tasks = [...(values.sub_tasks ?? []), aiSubtask]

    openForSubtask(sub_tasks.length - 1, { title: values.title, sub_tasks })
  }

  const handleAddSubtask = (type: number) => {
    switch (type) {
      case AI_IMAGE_BUTTON_TYPE:
        handleAddAiImageSubtask()
        break
      case keys.SUBTASK_TYPES.POLL_SUBTASK:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          is_multi_choice: false,
          type: keys.SUBTASK_TYPES.POLL_SUBTASK,
          options: [{ value: '' }, { value: '' }],
        } as PollSubtask)
        break
      case keys.SUBTASK_TYPES.PRIVATE_POLL:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          type: keys.SUBTASK_TYPES.PRIVATE_POLL,
        } as PollSubtask)
        break
      case keys.SUBTASK_TYPES.IMAGE_SUBTASK:
        subtaskActions.append?.({
          title: DEFAULT_T_TAKE_A_PHOTO,
          is_required: true,
          type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
          order_index: 0,
          content: {
            text: '',
            images: [],
            videos: [],
            files: [],
          },
        } as Subtask)
        break
      case keys.SUBTASK_TYPES.VIDEO_SUBTASKS:
        subtaskActions.append?.({
          title: DEFAULT_T_RECORD_A_VIDEO,
          is_required: true,
          type: keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
          order_index: 0,
          content: {
            text: '',
            images: [],
            videos: [],
            files: [],
          },
        } as Subtask)
        break
      case keys.SUBTASK_TYPES.LINK_SUBTASK:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          type: keys.SUBTASK_TYPES.LINK_SUBTASK,
          external_link: '',
          is_external_status_update: true,
        } as Subtask)
        break
      case keys.SUBTASK_TYPES.OPEN_QUESTION:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          is_numeric: false,
          type: keys.SUBTASK_TYPES.OPEN_QUESTION,
        } as OpenQuestionSubtask)
        break
      case keys.SUBTASK_TYPES.DESCRIPTION:
        // Info boxes are interleaved with actionable subtasks. `index_ref` is their position in
        // that combined list, so it must be correct before the editor's registration effect runs.
        // This also gives several consecutively-added Info boxes distinct insertion positions.
        const insertionIndex = subtaskActions.snapshotSubtasks?.().sub_tasks?.length ?? 0
        subtaskActions.append?.({
          type: keys.SUBTASK_TYPES.DESCRIPTION,
          text: '',
          images: [],
          videos: [],
          files: [],
          index_ref: insertionIndex,
        } as Content)
        break
      default:
        break
    }
  }

  // Filter buttons for VM Task (only Description, Image, AI Image, Video)
  const VM_TASK_ALLOWED_TYPES = [
    keys.SUBTASK_TYPES.DESCRIPTION,
    keys.SUBTASK_TYPES.IMAGE_SUBTASK,
    AI_IMAGE_BUTTON_TYPE,
    keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
  ]

  // AI review is a VM-only concept gated by the `ai_vm` flag — outside VM, or without the flag, the
  // widget renders no AI controls, so a subtask added here could never be edited or turned off again.
  const buttonsToShow = SUBTASK_BUTTONS.filter((btn) =>
    btn.type === AI_IMAGE_BUTTON_TYPE
      ? isVmTask && isAiVmEnabled
      : !isVmTask || VM_TASK_ALLOWED_TYPES.includes(btn.type)
  )

  return { buttons: buttonsToShow, addSubtask: handleAddSubtask }
}
